<template>
  <FormContainer>
    <template #title>Coordonnées du salarié(e)</template>
    <template #content>
      <div class="form__bloc">
        <WrapperInput
          id="field_132"
          :model-value="salariéEnCours?.adresse?.voie"
          @update:model-value="(value) => updateModule('adresse.voie', activeSalariéIndex!, value)"
          :label="LABEL_NUMERO_VOIE"
          class="col-12 col-md-18 col-sm-36"
          :placeholder="PLACEHOLDER_ADR_VOIE"
          :required="metaSalarieEnCours?.adresse?.voie?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.adresse?.voie?.$messageErreurDirty!"
          :maxlength="38"
          deletable
          :readonly
        />
        <WrapperInput
          id="field_133"
          :model-value="salariéEnCours?.adresse?.complement"
          @update:model-value="(value) => updateModule('adresse.complement', activeSalariéIndex!, value)"
          label="Complément d'adresse"
          class="col-12 col-md-18 col-sm-36"
          :placeholder="PLACEHOLDER_ADR_COMPLEMENT"
          :required="metaSalarieEnCours?.adresse?.complement?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.adresse?.complement?.$messageErreurDirty!"
          :maxlength="38"
          deletable
          :readonly
        />
        <WrapperAutocomplete
          id="field_134"
          key-id="codeInsee"
          key-label="intitule"
          :search="searchCommune"
          v-model="codePostalModel"
          :get-item-label="getCpCommuneLabel"
          label="Code postal"
          :search-min-length="3"
          :placeholder="PLACEHOLDER_ADR_CP"
          :readonly
          deletable
          class="col-4 col-md-6 col-sm-36"
          :strict="false"
          :required="metaSalarieEnCours?.adresse?.codePostal?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.adresse?.codePostal?.$messageErreurDirty!"
          :maxlength="5"
        />
        <WrapperAutocomplete
          id="field_135"
          key-id="codeInsee"
          key-label="intitule"
          :search="searchCommune"
          v-model="communeModel"
          :get-item-label="getCpCommuneLabel"
          label="Commune"
          :search-min-length="3"
          :placeholder="PLACEHOLDER_ADR_VILLE"
          :readonly
          deletable
          class="col-8 col-md-12 col-sm-36"
          :strict="false"
          :required="metaSalarieEnCours?.adresse?.commune?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.adresse?.commune?.$messageErreurDirty!"
          :maxlength="50"
        />
      </div>
      <div class="form__bloc">
        <WrapperInputTelephone
          id="field_136"
          :model-value="salariéEnCours?.telephone"
          @update:model-value="(value) => updateModule('telephone', activeSalariéIndex!, value)"
          label="Téléphone"
          :required="metaSalarieEnCours?.telephone?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.telephone?.$messageErreurDirty!"
          class="col-6 col-md-12 col-sm-36"
          :placeholder="PLACEHOLDER_TEL"
          :maxlength="10"
          deletable
          :readonly
        />
        <WrapperInput
          id="field_137"
          :model-value="salariéEnCours?.email"
          @update:model-value="(value) => updateModule('email', activeSalariéIndex!, value)"
          label="Email"
          :required="metaSalarieEnCours?.email?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.email?.$messageErreurDirty!"
          hint="64 caractères maximum"
          class="col-10 col-md-18 col-sm-36"
          :placeholder="PLACEHOLDER_EMAIL"
          :maxlength="64"
          deletable
          :readonly
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { inject } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useDemandeAFStore } from '@/features/interface/demande/af/useDemandeAFStore'
  import { AF_SALARIES_ACTIONS } from '@/features/interface/demande/af/steps/salarie/useSalarieActions'
  import { AF_ACTIONS } from '@/features/interface/demande/af/useDemandeAFActions.ts'
  import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus'
  import {
    FormContainer,
    LABEL_NUMERO_VOIE,
    PLACEHOLDER_ADR_VOIE,
    PLACEHOLDER_ADR_VILLE,
    PLACEHOLDER_ADR_COMPLEMENT,
    PLACEHOLDER_ADR_CP,
    PLACEHOLDER_EMAIL,
    PLACEHOLDER_TEL,
  } from '@/features/interface/demande/shared'
  import type { SalarieDTO } from '@/features/api/extranet/dto/Demande/AF'
  import {
    type CodePostalCommuneValue,
    useCodePostalCommune,
  } from '@/features/interface/composables/useCodePostalCommune'
  import type { AdresseDTO } from '@/features/api/extranet/dto/shared'

  const store = useDemandeAFStore()
  const { setupArrayUpdaterWithKey, setupCustomUpdater } = store

  const { readonly } = storeToRefs(store)
  const { activeSalariéIndex, salariéEnCours, metaSalarieEnCours } = inject(AF_SALARIES_ACTIONS)!
  const { searchCommune } = inject(AF_ACTIONS)!

  const { codePostalModel, communeModel, getCpCommuneLabel } = useCodePostalCommune({
    getCodePostal: () => salariéEnCours?.value?.adresse?.codePostal,
    getCommune: () => salariéEnCours?.value?.adresse?.commune,
    updateValues: setupCustomUpdater<CodePostalCommuneValue>((patch, value) => {
      const salarié = patch.salaries![activeSalariéIndex.value!]!
      if (!salarié.adresse) {
        salarié.adresse = {} as AdresseDTO
      }
      salarié.adresse.codePostal = value.code ?? salariéEnCours!.value!.adresse?.codePostal ?? null
      salarié.adresse.commune = value.commune ?? salariéEnCours!.value!.adresse?.commune ?? null
    }),
  })

  const updateModule = setupArrayUpdaterWithKey<SalarieDTO>('salaries')
</script>
